import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import type { FileMetadata } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';

const FileNameEditor = ({
  dealId,
  dealShortId,
  file,
  onStartSave,
  onSettled,
  className,
}: {
  dealId: Deal['id'];
  dealShortId: Deal['short_id'];
  file: FileMetadata;

  onStartSave?: () => void;
  onSettled?: () => void;
  className?: string;
}) => {
  const fileName = file.name || '';

  const [fileNameValue, setFileNameValue] = useState(fileName);

  const queryClient = useQueryClient();
  const updateFileNameMutation = useMutation({
    onMutate: async ({ fileName }) => {
      onStartSave?.();

      dealStore.updateFileName({
        id: dealId,
        fileName,
        fileId: file.id,
      });
    },
    mutationFn: async ({
      fileName,
    }: {
      fileName: Deal['files'][number]['name'];
    }) => {
      if (!fileName) return;

      // /:id/files/:file_id/edit
      const response = await pdkAxios
        .post(`/deals/${dealId}/files/${file.id}/edit`, {
          name: fileName,
        })
        .catch((e) => {
          console.error('Error updating file name', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      toast({
        title: `Successfully updated file name`,
        description: fileNameValue,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating file name',
        description: error.message,
      });

      console.error('Error updating file name', error);
    },
    onSettled: () => {
      onSettled?.();
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handleFileNameChange = useCallback(
    debounce((newFileName: string) => {
      updateFileNameMutation.mutate({
        fileName: newFileName,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (fileNameValue === fileName) return;

    if (isEmpty(fileNameValue)) {
      return;
    }

    handleFileNameChange(fileNameValue);
  }, [fileNameValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFileNameValue(e.target.value);
  }, []);

  useEffect(() => {
    setFileNameValue(fileName);
  }, [fileName]);

  return (
    <InlineTextInput
      key={`file-name-editor-${dealShortId}-input`}
      placeholder="File Name"
      value={fileNameValue}
      wrapperClassName="w-full"
      className={cn('w-full! text-ellipsis', className)}
      onChange={onChange}
    />
  );
};

export default FileNameEditor;
